/**
 * Gateway HTTP Client
 * T012: Implement GatewayClient with fetch, retry, timeout
 */

import type { GatewayConfig } from './config'
import type { RequestOptions, UploadProgressCallback } from './types'
import {
  type ApiError,
  parseError,
  createNetworkError,
  createTimeoutError,
  createUnknownError,
  isRetryableError,
} from './errors'

// ============================================================================
// Types
// ============================================================================

export interface GatewayClient {
  readonly config: GatewayConfig
  get<T>(path: string, options?: RequestOptions): Promise<T>
  post<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  put<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  patch<T>(path: string, body?: unknown, options?: RequestOptions): Promise<T>
  delete<T>(path: string, options?: RequestOptions): Promise<T>
  uploadFile(
    path: string,
    file: File | Blob,
    fields?: Record<string, string>,
    onProgress?: UploadProgressCallback
  ): Promise<Response>
}

// ============================================================================
// Constants
// ============================================================================

const RETRY_DELAYS = [1000, 2000, 4000] // Exponential backoff: 1s, 2s, 4s
const MAX_RETRIES = 3

// ============================================================================
// Client Implementation
// ============================================================================

/**
 * Create a GatewayClient instance
 */
export function createGatewayClient(config: GatewayConfig): GatewayClient {
  return {
    config,

    async get<T>(path: string, options?: RequestOptions): Promise<T> {
      return request<T>(config, 'GET', path, undefined, options)
    },

    async post<T>(
      path: string,
      body?: unknown,
      options?: RequestOptions
    ): Promise<T> {
      return request<T>(config, 'POST', path, body, options)
    },

    async put<T>(
      path: string,
      body?: unknown,
      options?: RequestOptions
    ): Promise<T> {
      return request<T>(config, 'PUT', path, body, options)
    },

    async patch<T>(
      path: string,
      body?: unknown,
      options?: RequestOptions
    ): Promise<T> {
      return request<T>(config, 'PATCH', path, body, options)
    },

    async delete<T>(path: string, options?: RequestOptions): Promise<T> {
      return request<T>(config, 'DELETE', path, undefined, options)
    },

    async uploadFile(
      path: string,
      file: File | Blob,
      fields?: Record<string, string>,
      onProgress?: UploadProgressCallback
    ): Promise<Response> {
      return uploadWithProgress(config, path, file, fields, onProgress)
    },
  }
}

// ============================================================================
// Request Execution
// ============================================================================

async function request<T>(
  config: GatewayConfig,
  method: string,
  path: string,
  body?: unknown,
  options?: RequestOptions
): Promise<T> {
  const url = buildUrl(config.baseUrl, path, options?.params)
  const headers = buildHeaders(config, options?.headers, body)
  const timeout = options?.timeout ?? config.timeout

  const init: RequestInit = {
    method,
    headers,
    body: body !== undefined ? JSON.stringify(body) : undefined,
  }

  // Add credentials for OAuth mode
  if (config.authMode === 'oauth') {
    init.credentials = 'include'
  }

  // Execute with retry logic
  const shouldRetry = !options?.noRetry
  return executeWithRetry<T>(config, url, init, timeout, shouldRetry)
}

async function executeWithRetry<T>(
  config: GatewayConfig,
  url: string,
  init: RequestInit,
  timeout: number,
  shouldRetry: boolean
): Promise<T> {
  let lastError: ApiError | null = null

  for (let attempt = 0; attempt <= MAX_RETRIES; attempt++) {
    try {
      // Apply request interceptor
      let request = new Request(url, init)
      if (config.onRequest) {
        request = await config.onRequest(request)
      }

      // Execute fetch with timeout
      const response = await fetchWithTimeout(request, timeout)

      // Apply response interceptor
      let processedResponse = response
      if (config.onResponse) {
        processedResponse = await config.onResponse(response)
      }

      // Check for errors
      if (!processedResponse.ok) {
        const error = await parseError(processedResponse)

        // Don't retry client errors (4xx)
        if (!shouldRetry || !isRetryableError(error)) {
          if (config.onError) {
            config.onError(error)
          }
          throw error
        }

        lastError = error

        // Retry with delay
        if (attempt < MAX_RETRIES) {
          await sleep(RETRY_DELAYS[attempt])
          continue
        }

        // Max retries reached
        if (config.onError) {
          config.onError(error)
        }
        throw error
      }

      // Parse JSON response
      const contentType = processedResponse.headers.get('content-type') || ''
      if (contentType.includes('application/json')) {
        return await processedResponse.json()
      }

      // Return empty object for no-content responses
      return {} as T
    } catch (error) {
      // Handle known API errors
      if (isApiErrorType(error)) {
        throw error
      }

      // Handle network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const networkError = createNetworkError(error.message)

        if (!shouldRetry || attempt >= MAX_RETRIES) {
          if (config.onError) {
            config.onError(networkError)
          }
          throw networkError
        }

        lastError = networkError
        await sleep(RETRY_DELAYS[attempt])
        continue
      }

      // Handle timeout errors
      if (error instanceof DOMException && error.name === 'AbortError') {
        const timeoutError = createTimeoutError(timeout)
        if (config.onError) {
          config.onError(timeoutError)
        }
        throw timeoutError
      }

      // Wrap unknown errors
      const unknownError = createUnknownError(error)
      if (config.onError) {
        config.onError(unknownError)
      }
      throw unknownError
    }
  }

  // Should not reach here, but throw last error if we do
  throw lastError ?? createUnknownError('Max retries exceeded')
}

async function fetchWithTimeout(
  request: Request,
  timeoutMs: number
): Promise<Response> {
  const controller = new AbortController()
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs)

  try {
    // Use fetch with signal override instead of cloning the request
    // Cloning a Request can cause the body to be lost in some environments
    return await fetch(request, { signal: controller.signal })
  } finally {
    clearTimeout(timeoutId)
  }
}

// ============================================================================
// File Upload with Progress
// ============================================================================

async function uploadWithProgress(
  config: GatewayConfig,
  path: string,
  file: File | Blob,
  fields?: Record<string, string>,
  onProgress?: UploadProgressCallback
): Promise<Response> {
  const url = buildUrl(config.baseUrl, path)
  const formData = new FormData()

  // Add file
  formData.append('file', file)

  // Add additional fields
  if (fields) {
    for (const [key, value] of Object.entries(fields)) {
      formData.append(key, value)
    }
  }

  // Use XHR if progress callback is provided (fetch doesn't support upload progress)
  if (onProgress) {
    return uploadWithXHR(config, url, formData, onProgress)
  }

  // Use fetch for simpler case
  const headers: Record<string, string> = {}

  if (config.authMode === 'apiKey' && config.apiKey) {
    headers['Authorization'] = `Bearer ${config.apiKey}`
  }

  const init: RequestInit = {
    method: 'POST',
    headers,
    body: formData,
  }

  if (config.authMode === 'oauth') {
    init.credentials = 'include'
  }

  const response = await fetch(url, init)

  if (!response.ok) {
    const error = await parseError(response)
    if (config.onError) {
      config.onError(error)
    }
    throw error
  }

  return response
}

function uploadWithXHR(
  config: GatewayConfig,
  url: string,
  formData: FormData,
  onProgress: UploadProgressCallback
): Promise<Response> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest()

    xhr.upload.addEventListener('progress', (event) => {
      if (event.lengthComputable) {
        onProgress(event.loaded, event.total)
      }
    })

    xhr.addEventListener('load', () => {
      const response = new Response(xhr.response, {
        status: xhr.status,
        statusText: xhr.statusText,
        headers: parseXHRHeaders(xhr.getAllResponseHeaders()),
      })

      if (xhr.status >= 200 && xhr.status < 300) {
        resolve(response)
      } else {
        parseError(response).then((error) => {
          if (config.onError) {
            config.onError(error)
          }
          reject(error)
        })
      }
    })

    xhr.addEventListener('error', () => {
      const error = createNetworkError('Upload failed')
      if (config.onError) {
        config.onError(error)
      }
      reject(error)
    })

    xhr.addEventListener('timeout', () => {
      const error = createTimeoutError(config.timeout)
      if (config.onError) {
        config.onError(error)
      }
      reject(error)
    })

    xhr.open('POST', url)
    xhr.timeout = config.timeout

    // Set auth header
    if (config.authMode === 'apiKey' && config.apiKey) {
      xhr.setRequestHeader('Authorization', `Bearer ${config.apiKey}`)
    }

    // Include cookies for OAuth
    if (config.authMode === 'oauth') {
      xhr.withCredentials = true
    }

    xhr.send(formData)
  })
}

// ============================================================================
// Helpers
// ============================================================================

function buildUrl(
  baseUrl: string,
  path: string,
  params?: Record<string, string | number | boolean | undefined>
): string {
  // Use window.location.origin as fallback for empty baseUrl (local dev with proxy)
  const origin = baseUrl || window.location.origin
  const url = new URL(path, origin)

  if (params) {
    for (const [key, value] of Object.entries(params)) {
      if (value !== undefined) {
        url.searchParams.set(key, String(value))
      }
    }
  }

  return url.toString()
}

function buildHeaders(
  config: GatewayConfig,
  customHeaders?: Record<string, string>,
  body?: unknown
): Record<string, string> {
  const headers: Record<string, string> = {
    ...customHeaders,
  }

  // Add content-type for JSON body
  if (body !== undefined) {
    headers['content-type'] = 'application/json'
  }

  // Add Authorization header for API key mode
  if (config.authMode === 'apiKey' && config.apiKey) {
    headers['Authorization'] = `Bearer ${config.apiKey}`
  }

  return headers
}

function parseXHRHeaders(headersString: string): Headers {
  const headers = new Headers()
  const lines = headersString.trim().split(/[\r\n]+/)

  for (const line of lines) {
    const parts = line.split(': ')
    const key = parts.shift()
    const value = parts.join(': ')
    if (key) {
      headers.append(key, value)
    }
  }

  return headers
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms))
}

function isApiErrorType(value: unknown): value is ApiError {
  return (
    value !== null &&
    typeof value === 'object' &&
    'errorKind' in value &&
    'status' in value
  )
}
